import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useScrollLock } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { KYCContext } from '~/components/providers/KYCProvider';
import { UserContext } from '~/components/providers/UserProvider';
import { Routes } from '~/domains/common/constants/routes';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import useProtectedGuard from '~/domains/protectionGuards/hooks/useProtectedGuard';
import { ProtectionGuardType } from '~/domains/protectionGuards/types';
import { getNotificationsCount } from '~/services/notifications/notifications-service.service.api';
import { isPathMatch } from '~/utils/isPathMatch';

function useConnectedAppShellHeader() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const router = useRouter();
  const isMobile = useIsMobile();
  const [, setScrollLocked] = useScrollLock(false);

  const { user } = useContext(UserContext);
  const { KYCStatus } = useContext(KYCContext);
  const { data, isLoading: isBalanceLoading } = useWalletBalance();
  const { openModalUnique } = useContext(RootModalsContext);

  const currentPath = router.asPath;

  // ============== NOTIFICATIONS ==============
  // eslint-disable-next-line local-rules/require-query-stale-time
  const { data: notificationsCountData } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: () => getNotificationsCount(),
    enabled: !!user?.id,
  });

  const notificationsCount = useMemo(
    () => notificationsCountData?.data?.count,
    [notificationsCountData]
  );

  const userWidgetProps = useMemo(
    () =>
      user
        ? {
            handle: user?.handle,
            avatar: user?.avatarUrl,
            balance: data?.availableBalance,
            notificationsCount,
            isBalanceLoading,
          }
        : null,
    [user, data, isBalanceLoading, notificationsCount]
  );

  // ============== MENU HANDLING ==============
  // Close the menu on each navigation change
  useEffect(() => {
    const handleRouteChangeStart = () => setIsUserMenuOpen(false);

    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => router.events.off('routeChangeStart', handleRouteChangeStart);
  }, [router.events]);

  const toggleUserMenu = useCallback(() => {
    setIsUserMenuOpen(!isUserMenuOpen);
  }, [isUserMenuOpen]);

  useEffect(() => {
    setScrollLocked(isMobile ? isUserMenuOpen : false);
  }, [isMobile, isUserMenuOpen]);

  // ============== DEPOSIT / WITHDRAWAL ==============
  const { isLoading: isProtectionLoading, runProtectionChecks } = useProtectedGuard({
    action: 'make a deposit or withdrawal',
    protectionLevels: [
      ProtectionGuardType.KYC,
      ProtectionGuardType.LOCATION,
      ProtectionGuardType.GEOCOMPLY,
    ],
  });

  const handleOpenDepositModal = useCallback(
    async (e) => {
      // Handling the case where the user is already on the deposit/withdrawal page
      if (isPathMatch(currentPath, Routes.deposit())) return setIsUserMenuOpen(false);
      if (isPathMatch(currentPath, Routes.withdraw())) return router.push(Routes.deposit());

      e.preventDefault();
      e.stopPropagation();
      setIsUserMenuOpen(false);

      const isAccessible = await runProtectionChecks();
      if (isAccessible) openModalUnique({ type: 'DepositModal' });
    },
    [runProtectionChecks, currentPath]
  );

  const handleOpenWithdrawalModal = useCallback(
    async (e) => {
      // Handling the case where the user is already on the deposit/withdrawal page
      if (isPathMatch(currentPath, Routes.withdraw())) return setIsUserMenuOpen(false);
      if (isPathMatch(currentPath, Routes.deposit())) return router.push(Routes.withdraw());

      e.preventDefault();
      e.stopPropagation();
      setIsUserMenuOpen(false);

      const isAccessible = await runProtectionChecks();
      if (isAccessible) openModalUnique({ type: 'WithdrawalModal' });
    },
    [runProtectionChecks, currentPath]
  );

  // Calculate cash balance from availableBalance - credits
  // Note: When backend provides a dedicated cashBalance field, use that instead
  const cashBalance = useMemo(
    () => (data?.availableBalance ?? 0) - (data?.credits ?? 0),
    [data?.availableBalance, data?.credits]
  );

  return {
    cashBalance,
    balance: data?.withdrawableBalance,
    credits: data?.credits,
    refundableBalance: data?.refundableBalance,
    playthroughCash: data?.playthroughCash,
    handleOpenDepositModal,
    handleOpenWithdrawalModal,
    isBalanceLoading,
    isProtectionLoading,
    isUserMenuOpen,
    KYCStatus,
    notificationsCount,
    promotionsCount: undefined, // TODO: V2 - for vouchers
    toggleUserMenu,
    user,
    userWidgetProps,
  };
}

export default useConnectedAppShellHeader;
